import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Home, LayoutDashboard, Truck, FileText, Users, Wrench, Calendar, CalendarDays, ClipboardList, ClipboardCheck,
  Phone, Package, MapPin, Building2, DollarSign, Receipt, ReceiptText, BarChart3, Bell, Settings, Shield, Clock, Menu, ChevronRight,
  Layers, ScrollText, Hammer, Handshake, TrendingUp,
} from 'lucide-react';
import { apiClient } from '../api/client';
import type { ApiResponse } from '../types/api-manifest';
import {
  buildVerticalSidebarStructure,
  usesVerticalSidebar,
  type ResolvedVerticalGroup,
  type ResolvedVerticalNavItem,
  type VerticalSidebarSection,
} from '../config/verticalNav';
import { useDownholeFeature } from '../hooks/useDownholeSettings';

/**
 * THE PHONE NAV FOR TRUCKING, RENTALS AND DOWNHOLE TENANTS: THE SIDEBAR, SPLIT IN TWO (#803).
 *
 * The bottom bar and /more used to keep their own lists, so the phone and the
 * desktop drifted: the bar declared its own trucking and rental tabs, and /more
 * listed only casing items, which left Safety, Fleet, Weight Tickets and every
 * rental tool with no way in on a phone. Both now read the desktop sidebar's
 * resolved groups (`buildVerticalSidebarStructure`: vertical guards, trucking
 * feature flags, one entry per page), and split them:
 *
 *   - the BAR is the FAVORITES group, up to four tabs, then More;
 *   - MORE is every other group, in sidebar order, plus any favourite past the
 *     fourth.
 *
 * So every page the sidebar shows is on the bar or in More, and nothing else is.
 * `MoreScreen.vertical-nav.test.tsx` pins that for trucking-only, rentals-only
 * and both.
 *
 * WHO GETS IT: a tenant with trucking, surface rentals or downhole tools and NOT casing, the
 * same tenants who get this sidebar on desktop (HomeScreen). A casing tenant's
 * bar and More are untouched, byte for byte, including one that also holds
 * trucking or rentals.
 */

type Icon = React.ComponentType<{ size?: number; className?: string }>;

/** Every lucide name verticalNav.ts and the shared CASING_NAV items it borrows use. */
export const PHONE_NAV_ICON: Record<string, Icon> = {
  Home, LayoutDashboard, Truck, FileText, Users, Wrench, Calendar, CalendarDays, ClipboardList, ClipboardCheck,
  Phone, Package, MapPin, Building2, DollarSign, Receipt, ReceiptText, BarChart3, Bell, Settings, Shield, Clock,
  Layers, ScrollText, Hammer, Handshake, TrendingUp,
};
export const phoneNavIcon = (name: string): Icon => PHONE_NAV_ICON[name] ?? Menu;

export const PHONE_BAR_MAX = 4;

/**
 * Positive guards, one per More section. The groups are already resolved per
 * vertical, so an empty one never renders; the guard says WHY a section exists
 * for this tenant and keeps a section from appearing for a vertical it does not
 * belong to.
 */
const anyVertical = (has: (k: string) => boolean) =>
  has('trucking') || has('surface_rentals') || has('downhole_tools') || has('pressure_testing') || has('ndt_inspection') ||
  has('pipeline_facility') || has('well_testing') || has('earthworks') || has('well_servicing') || has('wireline_slickline');
const SECTION_GUARD: Record<Exclude<VerticalSidebarSection, 'FAVORITES'>, (has: (k: string) => boolean) => boolean> = {
  OPERATIONS: anyVertical,
  'WELL TESTING': (has) => has('well_testing'),
  EARTHWORKS: (has) => has('earthworks'),
  'WELL SERVICING': (has) => has('well_servicing'),
  'WIRELINE & SLICKLINE': (has) => has('wireline_slickline'),
  REPORTS: anyVertical,
  'TRUCKING TOOLS': (has) => has('trucking'),
  'TRUCKS & DRIVERS': (has) => has('surface_rentals'),
  'RENTAL TOOLS': (has) => has('surface_rentals'),
  COMPANY: anyVertical,
  // The CRM is cross-vertical; the role filter (navPathOpenForRole) decides who sees it.
  SALES: anyVertical,
  FINANCE: anyVertical,
  SYSTEM: anyVertical,
};

/** The heading shown over a More section: the sidebar's row label, or its section name. */
const sectionTitle = (g: ResolvedVerticalGroup) =>
  g.kind === 'row' && g.rowLabel ? g.rowLabel : g.section.charAt(0) + g.section.slice(1).toLowerCase();

export interface PhoneMoreSection {
  key: VerticalSidebarSection;
  title: string;
  items: ResolvedVerticalNavItem[];
}

/** Split resolved sidebar groups into the bar and the More sections. Pure, so it can be tested alone. */
export function splitPhoneNav(
  groups: ResolvedVerticalGroup[],
  hasVertical: (key: string) => boolean,
): { bar: ResolvedVerticalNavItem[]; more: PhoneMoreSection[] } {
  const favourites = groups.find((g) => g.section === 'FAVORITES')?.items ?? [];
  const bar = favourites.slice(0, PHONE_BAR_MAX);
  const more: PhoneMoreSection[] = [];
  const overflow = favourites.slice(PHONE_BAR_MAX);
  if (overflow.length) more.push({ key: 'FAVORITES', title: 'Favorites', items: overflow });
  for (const g of groups) {
    if (g.section === 'FAVORITES') continue;
    if (!SECTION_GUARD[g.section](hasVertical)) continue;
    more.push({ key: g.section, title: sectionTitle(g), items: g.items });
  }
  return { bar, more };
}

/** True for the tenants who get the vertical sidebar, and so this phone nav. */
export const usesVerticalPhoneNav = (hasVertical: (key: string) => boolean) => usesVerticalSidebar(hasVertical);

/** The bar and More for this tenant, from the same request (and cache entry) the sidebar uses. */
export function useVerticalPhoneNav(hasVertical: (key: string) => boolean) {
  const enabled = usesVerticalPhoneNav(hasVertical);
  const { data: truckingFeatures } = useQuery({
    queryKey: ['settings', 'trucking'],
    queryFn: () => apiClient.get<ApiResponse<'GET /api/v1/settings/trucking'>>('/settings/trucking').then((r) => r.data?.data ?? r.data ?? {}),
    staleTime: 60_000,
    enabled: enabled && hasVertical('trucking'),
  });
  const ddMwdEnabled = useDownholeFeature('dd_mwd');
  if (!enabled) return { enabled, bar: [], more: [] };
  return { enabled, ...splitPhoneNav(buildVerticalSidebarStructure(hasVertical, truckingFeatures, { dd_mwd: ddMwdEnabled }), hasVertical) };
}

/**
 * The More screen's sections for a trucking / rentals tenant: the sidebar's
 * groups after FAVORITES, each under its sidebar name. Rows are buttons, so a
 * keyboard and a screen reader reach them.
 */
export function VerticalMoreSections({ sections, onOpen }: { sections: PhoneMoreSection[]; onOpen: (path: string) => void }) {
  return (
    <div data-testid="vertical-more" className="space-y-5">
      {sections.map((section) => (
        <section key={section.key} aria-labelledby={`more-${section.key}`}>
          <h2 id={`more-${section.key}`} className="mb-2 px-1 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
            {section.title}
          </h2>
          <ul className="space-y-2">
            {section.items.map((item) => {
              const ItemIcon = phoneNavIcon(item.icon);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    data-testid="vertical-more-item"
                    onClick={() => onOpen(item.path)}
                    className="flex w-full items-center justify-between rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left transition-colors hover:bg-fieldiq-dark/50"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-fi-control bg-fieldiq-dark" aria-hidden="true">
                        <ItemIcon size={20} className="text-fieldiq-text" />
                      </span>
                      <span className="font-semibold text-fieldiq-text">{item.label}</span>
                    </span>
                    <ChevronRight size={20} className="text-fieldiq-muted" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
